import React, { useState } from 'react';
import { AlertCircle, Loader2, Plus, Search } from 'lucide-react';
import { useDialog } from '../components/AppDialog';
import { useJobTracker } from './hooks/useJobTracker';
import { JobBoard } from './components/JobBoard';
import { JobToolbar } from './components/JobToolbar';
import { JobEmptyState } from './components/JobEmptyState';
import { JobFormModal } from './components/JobFormModal';
import { JobDetailsModal } from './components/JobDetailsModal';
import { T } from './constants';

/**
 * Job Tracker — entry point for the module.
 *
 * Rendered by DashboardPage as the `job_tracker` view; it inherits the
 * dashboard workspace padding, so no outer page chrome here.
 *
 * @param {() => void} onFindJobs  switches the dashboard to the Job Search view
 */
export const JobTrackerPage = ({ onFindJobs = () => {} }) => {
  const { showConfirm } = useDialog();
  const {
    jobs, filtered, byStage,
    loading, saving, error, setError,
    createJob, updateJob, deleteJob, moveJob,
    search, setSearch,
    stageFilter, setStageFilter,
    dateFilter, setDateFilter,
    hasFilters, clearFilters,
  } = useJobTracker();

  // null = closed; { job } = edit; { stage } = add into that column
  const [formState, setFormState] = useState(null);
  const [detailsJob, setDetailsJob] = useState(null);

  const openAdd = (stage = 'applied') => setFormState({ stage });
  const openEdit = (job) => { setDetailsJob(null); setFormState({ job }); };

  const handleSave = async (data) => {
    if (formState?.job) await updateJob(formState.job._id, data);
    else await createJob(data);
    setFormState(null);
  };

  const handleDelete = async (job) => {
    const ok = await showConfirm(
      `Delete your ${job.title} application at ${job.company}? This cannot be undone.`,
      { title: 'Delete Application', confirmLabel: 'Delete', danger: true },
    );
    if (!ok) return;
    setDetailsJob(null);
    try { await deleteJob(job._id); } catch { /* surfaced via error banner */ }
  };

  const handleMove = async (id, stage) => {
    const moved = await moveJob(id, stage);
    // Only reflect the new stage in the open modal once the server agreed —
    // otherwise a rejected move still looked like it had succeeded.
    if (moved) setDetailsJob(prev => (prev && prev._id === id ? { ...prev, ...moved } : prev));
  };

  const isEmpty = !loading && jobs.length === 0;

  return (
    <div style={{ fontFamily: T.font }}>
      <style>{`
        @keyframes jtSpin { to { transform: rotate(360deg); } }
        .jt-card:hover { border-color: #D9DEE6 !important; box-shadow: 0 8px 22px rgba(15,23,42,0.10) !important; transform: translateY(-1px); }
        .jt-card:active { cursor: grabbing; }
        .jt-board { display: flex; gap: 14px; overflow-x: auto; padding-bottom: 12px; align-items: flex-start; scrollbar-width: thin; }
        .jt-board::-webkit-scrollbar { height: 8px; }
        .jt-board::-webkit-scrollbar-thumb { background: #DDE3EA; border-radius: 8px; }
        .jt-board::-webkit-scrollbar-track { background: transparent; }
        .jt-board-col { flex: 0 0 262px; max-width: 262px; }
        .jt-board-col > section { height: 100%; }
        .jt-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 13px; }

        /* Tablet: narrower columns so 2-3 fit before scrolling */
        @media (max-width: 1024px) {
          .jt-board-col { flex: 0 0 242px; max-width: 242px; }
        }

        /* Mobile: one stage at a time, chosen from the picker */
        @media (max-width: 720px) {
          .jt-stage-picker { display: flex !important; }
          .jt-board { display: block; overflow-x: visible; }
          .jt-board-col { display: none; max-width: none; }
          .jt-board-col-active { display: block; }
          .jt-form-grid { grid-template-columns: 1fr; }
          .jt-form-grid > div { grid-column: span 1 !important; }
        }
      `}</style>

      {/* ── Header ── */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: 14, flexWrap: 'wrap', marginBottom: 18,
      }}>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: T.ink, letterSpacing: -0.3 }}>
            Job Tracker
          </h1>
          <p style={{ margin: '5px 0 0', fontSize: 13.5, color: T.muted, lineHeight: 1.6, maxWidth: 520 }}>
            Keep every application organized and never lose track of your next opportunity.
          </p>
        </div>

        <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap', flexShrink: 0 }}>
          <button type="button" onClick={onFindJobs}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              background: T.surface, color: T.body, border: `1.5px solid ${T.line}`,
              borderRadius: 10, padding: '9px 17px', fontSize: 13, fontWeight: 600,
              cursor: 'pointer', fontFamily: T.font, transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#CBD5E1'; e.currentTarget.style.background = T.canvas; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = T.line; e.currentTarget.style.background = T.surface; }}>
            <Search size={14} strokeWidth={2.2} /> Find Jobs
          </button>

          <button type="button" onClick={() => openAdd()}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              background: T.brandGrad, color: 'white', border: 'none',
              borderRadius: 10, padding: '9px 19px', fontSize: 13, fontWeight: 700,
              cursor: 'pointer', fontFamily: T.font, boxShadow: T.shadowBrand,
              transition: 'transform 0.15s, box-shadow 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(239,29,40,0.45)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = T.shadowBrand; }}>
            <Plus size={15} strokeWidth={2.5} /> Add Job
          </button>
        </div>
      </div>

      {/* ── Error banner ── */}
      {error && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 9, marginBottom: 14,
          background: T.brandSoft, border: `1px solid ${T.brandBorder}`, borderRadius: 10,
          padding: '10px 14px', fontSize: 12.5, color: T.brandDark, fontWeight: 600,
        }}>
          <AlertCircle size={15} style={{ flexShrink: 0 }} />
          <span style={{ flex: 1 }}>{error}</span>
          <button type="button" onClick={() => setError('')}
            style={{ border: 'none', background: 'transparent', color: T.brandDark, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: T.font }}>
            Dismiss
          </button>
        </div>
      )}

      {/* ── Content ── */}
      {loading ? (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 11, padding: '70px 20px', color: T.muted,
        }}>
          <Loader2 size={26} color={T.brand} style={{ animation: 'jtSpin 1s linear infinite' }} />
          <span style={{ fontSize: 13 }}>Loading your applications…</span>
        </div>
      ) : isEmpty ? (
        <JobEmptyState onAdd={() => openAdd()} onFindJobs={onFindJobs} />
      ) : (
        <>
          <JobToolbar
            search={search} onSearch={setSearch}
            stageFilter={stageFilter} onStageFilter={setStageFilter}
            dateFilter={dateFilter} onDateFilter={setDateFilter}
            hasFilters={hasFilters} onClear={clearFilters}
            total={jobs.length} showing={filtered.length}
          />
          <JobBoard
            byStage={byStage}
            onOpenJob={setDetailsJob}
            onAddToStage={openAdd}
            onMoveJob={handleMove}
          />
        </>
      )}

      {/* ── Modals ── */}
      {formState && (
        <JobFormModal
          job={formState.job}
          defaultStage={formState.stage || 'applied'}
          saving={saving}
          onSave={handleSave}
          onClose={() => setFormState(null)}
        />
      )}

      {detailsJob && !formState && (
        <JobDetailsModal
          job={detailsJob}
          onEdit={openEdit}
          onDelete={handleDelete}
          onMove={handleMove}
          onClose={() => setDetailsJob(null)}
        />
      )}
    </div>
  );
};

export default JobTrackerPage;
